/**
 * Pending Users Approval Page
 * Admin page for reviewing and approving/rejecting external user registrations
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useTenant } from '../../contexts/TenantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate } from '../../utils/formatters';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const PENDING_USER_COLUMNS = [
  { key: 'email',             label: 'Email',            align: 'left', getValue: (u) => u.email },
  { key: 'full_name',         label: 'Full Name',        align: 'left', getValue: (u) => u.full_name },
  { key: 'request_type',      label: 'Type',             align: 'left', getValue: (u) => u.request_type },
  { key: 'tenant_name',       label: 'Tenant',           align: 'left', getValue: (u) => u.tenant_name },
  { key: 'reason_for_access', label: 'Reason for Access',align: 'left', getValue: (u) => u.reason_for_access },
  { key: 'created_at',        label: 'Submitted',        align: 'left', getValue: (u) => u.created_at },
  { key: 'status',            label: 'Status',           align: 'left', getValue: (u) => u.status },
];

const EXTERNAL_USER_COLUMNS = [
  { key: 'email',             label: 'Email',            align: 'left', getValue: (u) => u.email },
  { key: 'name',              label: 'Name',             align: 'left', getValue: (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() },
  { key: 'tenant_name',       label: 'Tenant',           align: 'left', getValue: (u) => u.tenant_name },
  { key: 'investor_name',     label: 'Linked Investor',  align: 'left', getValue: (u) => u.investor_name },
  { key: 'external_status',   label: 'Status',           align: 'left', getValue: (u) => u.external_status || 'active' },
  { key: 'last_login',        label: 'Last Login',       align: 'left', getValue: (u) => u.last_login },
];

const PendingUsersPage = () => {
  const { tenant: currentTenant } = useTenant();
  const isMasterTenant = currentTenant?.code === 'master';

  const [pendingUsers, setPendingUsers] = useState([]);
  const [externalUsers, setExternalUsers] = useState([]);
  const pendingSort = useTableSort();
  const externalSort = useTableSort();
  const [investors, setInvestors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const notify = useNotification();

  // Dialog states
  const [approveDialog, setApproveDialog] = useState({ open: false, user: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null });
  const [linkDialog, setLinkDialog] = useState({ open: false, user: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [dialogInvestors, setDialogInvestors] = useState([]);
  const [loadingDialogInvestors, setLoadingDialogInvestors] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Internal approval state
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [loadingRoles, setLoadingRoles] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [pendingRes, externalRes, investorsRes, tenantsRes] = await Promise.all([
        apiGet('/admin/pending-users'),
        apiGet('/admin/external-users'),
        apiGet('/stakeholders/investors').catch(() => ({ data: [] })), // May fail on master tenant (not provisioned)
        apiGet('/tenants').catch(() => ({ data: [] })), // Tenants may not be accessible
      ]);

      setPendingUsers(pendingRes.data || pendingRes || []);
      setExternalUsers(externalRes.data || externalRes || []);
      setInvestors(investorsRes.data || investorsRes || []);
      // Filter out master tenant from options
      const allTenants = tenantsRes.data || tenantsRes || [];
      setTenants(allTenants.filter(t => t.code !== 'master' && t.code !== 'default' && t.status === 'active'));
    } catch (err) {
      notify.error('Failed to load data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetApprovalState = () => {
    setApproveDialog({ open: false, user: null });
    setSelectedInvestor(null);
    setSelectedTenant(null);
    setIsTenantAdmin(false);
    setRoles([]);
    setSelectedRoleId('');
  };

  // Load assignable RBAC roles for the selected tenant (system roles + that
  // tenant's own roles). The chosen role is assigned to the user on approval;
  // permissions are resolved entirely from roles (RBAC).
  const handleTenantSelect = async (tenant) => {
    setSelectedTenant(tenant);
    setSelectedRoleId(''); // Reset role when tenant changes

    if (!tenant) {
      setRoles([]);
      return;
    }

    try {
      setLoadingRoles(true);
      const response = await apiGet(`/admin/roles?tenant_id=${tenant.id}`);
      setRoles(response.data || response || []);
    } catch (err) {
      logger.warn('Failed to load roles', { message: err.message, status: err.response?.status });
      // Fallback to empty - admin can still approve; user gets the default Viewer role
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog.user) return;

    const user = approveDialog.user;
    const isInternal = user.request_type === 'internal_access_request';

    // Validate required fields
    if (isInternal && !selectedTenant) {
      notify.error('Please select a tenant for this internal user');
      return;
    }

    try {
      setActionLoading(true);

      const payload = isInternal
        ? {
            tenant_id: selectedTenant?.id,
            role_id: selectedRoleId || null,
            is_admin: isTenantAdmin,
          }
        : {
            linked_investor_id: selectedInvestor?.id || null,
          };

      await apiPost(`/admin/pending-users/${user.id}/approve`, payload);
      notify.success(`User ${user.email} has been approved.`);
      resetApprovalState();
      loadData();
    } catch (err) {
      notify.error('Failed to approve user: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.user) return;

    try {
      setActionLoading(true);
      await apiPost(`/admin/pending-users/${rejectDialog.user.id}/reject`, {
        rejection_reason: rejectionReason,
      });
      notify.success(`User ${rejectDialog.user.email} has been rejected.`);
      setRejectDialog({ open: false, user: null });
      setRejectionReason('');
      loadData();
    } catch (err) {
      notify.error('Failed to reject user: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const openLinkDialog = async (user) => {
    setLinkDialog({ open: true, user });
    setSelectedInvestor(null);
    setDialogInvestors([]);

    if (user.tenant_id) {
      try {
        setLoadingDialogInvestors(true);
        const res = await apiGet(`/admin/external-users/investors-for-tenant/${user.tenant_id}`);
        setDialogInvestors(res.data || res || []);
      } catch (err) {
        logger.error('Failed to load investors for tenant', { message: err.message, status: err.response?.status });
        setDialogInvestors([]);
      } finally {
        setLoadingDialogInvestors(false);
      }
    }
  };

  const handleLinkInvestor = async () => {
    if (!linkDialog.user || !selectedInvestor) return;

    try {
      setActionLoading(true);
      await apiPut(`/admin/external-users/${linkDialog.user.id}/link-investor`, {
        investor_id: selectedInvestor.id,
        tenant_id: linkDialog.user.tenant_id,
      });
      notify.success(`User ${linkDialog.user.email} has been linked to investor ${selectedInvestor.name}.`);
      setLinkDialog({ open: false, user: null });
      setSelectedInvestor(null);
      setDialogInvestors([]);
      loadData();
    } catch (err) {
      notify.error('Failed to link investor: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
    }
  };

  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: null });

  const handleDeactivateUser = async (user) => {
    try {
      setActionLoading(true);
      await apiPut(`/admin/external-users/${user.id}/deactivate`);
      notify.success(`User ${user.email} has been deactivated.`);
      setConfirmDialog({ open: false, user: null, action: null });
      loadData();
    } catch (err) {
      notify.error('Failed to deactivate user: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateUser = async (user) => {
    try {
      setActionLoading(true);
      await apiPut(`/admin/external-users/${user.id}/reactivate`);
      notify.success(`User ${user.email} has been reactivated.`);
      loadData();
    } catch (err) {
      notify.error('Failed to reactivate user: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      setActionLoading(true);
      await apiDelete(`/admin/external-users/${user.id}`);
      notify.success(`User ${user.email} has been permanently deleted.`);
      setConfirmDialog({ open: false, user: null, action: null });
      loadData();
    } catch (err) {
      notify.error('Failed to delete user: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusColors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return (
      <Chip
        label={status}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  const getRequestTypeChip = (requestType, authProvider) => {
    if (requestType === 'internal_access_request') {
      return (
        <Chip
          icon={<PersonAddIcon />}
          label="Access Request"
          color="info"
          size="small"
          variant="outlined"
        />
      );
    }
    // Default: external registration (Keycloak)
    return (
      <Chip
        icon={<LoginIcon />}
        label="Registration"
        color="secondary"
        size="small"
        variant="outlined"
      />
    );
  };

  const getTenantChip = (user) => {
    if (user.tenant_name) {
      return (
        <Chip
          icon={<BusinessIcon />}
          label={user.tenant_name}
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }
    // Unassigned - admin will assign during approval
    return (
      <Tooltip title="Admin will assign tenant during approval">
        <Chip
          icon={<HelpOutlineIcon />}
          label="Unassigned"
          size="small"
          color="default"
          variant="outlined"
        />
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>


      {/* Pending Registrations */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Pending Registrations ({pendingUsers.filter(u => u.status === 'pending').length})
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {PENDING_USER_COLUMNS
                  .filter((col) => isMasterTenant || col.key !== 'tenant_name')
                  .map((col) => (
                    <SortableHeaderCell key={col.key} sort={pendingSort} columnKey={col.key} align={col.align}>
                      {col.label}
                    </SortableHeaderCell>
                  ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingUsers.filter(u => u.status === 'pending').length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMasterTenant ? 8 : 7} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No pending registrations
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pendingSort.sortRows(
                  pendingUsers.filter(u => u.status === 'pending'),
                  PENDING_USER_COLUMNS
                ).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.full_name || '-'}</TableCell>
                      <TableCell>{getRequestTypeChip(user.request_type, user.auth_provider)}</TableCell>
                      {isMasterTenant && <TableCell>{getTenantChip(user)}</TableCell>}
                      <TableCell>{user.reason_for_access || '-'}</TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>{getStatusChip(user.status)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Approve">
                          <IconButton
                            color="success"
                            onClick={() => setApproveDialog({ open: true, user })}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            color="error"
                            onClick={() => setRejectDialog({ open: true, user })}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Approved External Users */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            External Users ({externalUsers.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {EXTERNAL_USER_COLUMNS.map((col) => (
                  <SortableHeaderCell key={col.key} sort={externalSort} columnKey={col.key} align={col.align}>
                    {col.label}
                  </SortableHeaderCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {externalUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No external users
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                externalSort.sortRows(externalUsers, EXTERNAL_USER_COLUMNS).map((user) => (
                  <TableRow key={user.id} sx={{
                    opacity: user.external_status === 'inactive' ? 0.6 : 1,
                  }}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.first_name || user.last_name
                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {user.tenant_name ? (
                        <Chip
                          icon={<BusinessIcon />}
                          label={user.tenant_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.investor_name ? (
                        <Chip
                          icon={<PersonIcon />}
                          label={user.investor_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography color="warning.main" variant="body2">
                          Not linked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.external_status || 'active'}
                        color={
                          user.external_status === 'inactive' ? 'error'
                          : user.external_status === 'pending' ? 'warning'
                          : 'success'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.last_login ? formatDate(user.last_login) : 'Never'}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openLinkDialog(user)}
                        >
                          {user.linked_investor_id ? 'Change Investor' : 'Link Investor'}
                        </Button>
                        {user.external_status !== 'inactive' ? (
                          <Tooltip title="Deactivate user (disable login)">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setConfirmDialog({ open: true, user, action: 'deactivate' })}
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reactivate user">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReactivateUser(user)}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Permanently delete user">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setConfirmDialog({ open: true, user, action: 'delete' })}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Approve Dialog - Different content based on request type */}
      <Dialog
        open={approveDialog.open}
        onClose={resetApprovalState}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approveDialog.user?.request_type === 'internal_access_request'
            ? 'Approve Access Request'
            : 'Approve User Registration'}
        </DialogTitle>
        <DialogContent>
          {/* User Info */}
          <Box sx={{ mb: 2 }}>
            <Typography>
              Approving <strong>{approveDialog.user?.email}</strong>
            </Typography>
            {approveDialog.user?.full_name && (
              <Typography variant="body2" color="text.secondary">
                {approveDialog.user.full_name}
              </Typography>
            )}
            {approveDialog.user?.reason_for_access && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Reason:</strong> {approveDialog.user.reason_for_access}
                </Typography>
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* External User - Investor Linking */}
          {approveDialog.user?.request_type !== 'internal_access_request' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Link to Investor (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an investor to link this user to. This determines which contracts they can view.
              </Typography>
              <Autocomplete
                options={investors}
                getOptionLabel={(option) => `${option.name} (${option.type || 'Unknown type'})`}
                value={selectedInvestor}
                onChange={(e, newValue) => setSelectedInvestor(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Investor"
                    placeholder="Search investors..."
                    fullWidth
                  />
                )}
              />
            </>
          )}

          {/* Internal User - Tenant & Permissions */}
          {approveDialog.user?.request_type === 'internal_access_request' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assign to Tenant *
              </Typography>
              <Autocomplete
                options={tenants}
                getOptionLabel={(option) => option.name}
                value={selectedTenant}
                onChange={(e, newValue) => handleTenantSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Tenant"
                    placeholder="Search tenants..."
                    fullWidth
                    required
                    error={!selectedTenant}
                    helperText={!selectedTenant ? 'Tenant selection is required' : ''}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isTenantAdmin}
                    onChange={(e) => setIsTenantAdmin(e.target.checked)}
                  />
                }
                label="Tenant Administrator"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Role
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedTenant
                  ? `Choose the role to assign in ${selectedTenant.name}. Permissions are determined by the role. Leave blank to assign the default Viewer role.`
                  : 'Select a tenant to choose a role.'}
              </Typography>

              {loadingRoles && <CircularProgress size={20} sx={{ mb: 2 }} />}

              {!loadingRoles && (
                <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedTenant}>
                  <InputLabel id="approve-role-label">Role</InputLabel>
                  <Select
                    labelId="approve-role-label"
                    label="Role"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Default (Viewer)</em>
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                        {(role.is_system_role || role.tenant_id === null) ? ' (Global)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetApprovalState}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={
              actionLoading ||
              (approveDialog.user?.request_type === 'internal_access_request' && !selectedTenant)
            }
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, user: null })}>
        <DialogTitle>Reject User Registration</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Rejecting access for <strong>{rejectDialog.user?.email}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Provide a reason for rejection (optional but recommended)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialog({ open: false, user: null });
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Investor Dialog */}
      <Dialog
        open={linkDialog.open}
        onClose={() => {
          setLinkDialog({ open: false, user: null });
          setSelectedInvestor(null);
          setDialogInvestors([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link User to Investor</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Link <strong>{linkDialog.user?.email}</strong> to an investor record.
            This determines which contracts and data they can access.
          </Typography>
          {linkDialog.user?.tenant_name && (
            <Chip
              icon={<BusinessIcon />}
              label={`Tenant: ${linkDialog.user.tenant_name}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
            />
          )}
          {!linkDialog.user?.tenant_id ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This user is not assigned to a tenant. Please assign them to a tenant before linking an investor.
            </Alert>
          ) : loadingDialogInvestors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Autocomplete
              options={dialogInvestors}
              getOptionLabel={(option) => `${option.name} (${option.type || 'Unknown type'})`}
              value={selectedInvestor}
              onChange={(e, newValue) => setSelectedInvestor(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Investor"
                  placeholder="Search investors..."
                  fullWidth
                />
              )}
              sx={{ mt: 2 }}
              noOptionsText="No investors found in this tenant"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLinkDialog({ open: false, user: null });
            setSelectedInvestor(null);
            setDialogInvestors([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkInvestor}
            disabled={actionLoading || !selectedInvestor || !linkDialog.user?.tenant_id}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Link Investor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deactivate/Delete Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, user: null, action: null })}
      >
        <DialogTitle>
          {confirmDialog.action === 'delete' ? 'Permanently Delete User' : 'Deactivate User'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.action === 'delete' ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              This action is irreversible. The user and all associated data (contract assignments, tenant access, and portal credentials) will be permanently deleted.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The user will be unable to log in. You can reactivate them later.
            </Alert>
          )}
          <Typography>
            Are you sure you want to {confirmDialog.action} <strong>{confirmDialog.user?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, user: null, action: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (confirmDialog.action === 'delete') {
                handleDeleteUser(confirmDialog.user);
              } else {
                handleDeactivateUser(confirmDialog.user);
              }
            }}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : (
              confirmDialog.action === 'delete' ? 'Delete Permanently' : 'Deactivate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingUsersPage;
